// Phase-02-deferred hooks (they need the StatsFilterKit engine, ported in Phase 04). Thin
// wrappers over the pure Filter layer + a synchronous snapshot read today; at the Track-2
// cutover only these bodies change (fetch a filtered-population endpoint and adapt to the
// same ProtoFilterResult shape). Consumed by FilterModule (04g-4b bio chips + results,
// 04g-4c the metric-range sliders).
//
// `useFilterBoard` is what the widget reads: the snapshot path is the hooks below, unchanged;
// with ?live=1 the same board comes from the shared group (`useLiveGroupFile` — her league's
// Stats file), matched by Ben's own computeFilterMatches (`live/filter.ts`).

import { chipIsOn } from "../engines/stats-filter-kit.js";
import { leagueOptionInfo } from "../modules/CountryFlag.js";
import type { OptionInfo } from "../modules/FilterPopovers.js";
import {
  chipOptionsOfGroups,
  filesOfGroups,
  groupsKey,
  playersOfGroups,
  refPlayerOf,
  useLiveLeagueGroups,
} from "./useLeagueGroups.js";
import { chipsWithData } from "../live/stats-files.js";
import { useMemo } from "react";
import type { ProtoFilterResult, ProtoPlayerMetric } from "@catbento/shared";
import { readSnapshot, type RawPlayerRecord } from "../data/snapshot.js";
import { getMetricCategories } from "../data/stats.js";
import { playerById, playerNameOf, type PlayerId } from "../data/players.js";
import {
  filterSeasonCodes,
  buildMetricByKey,
  metricDomain,
  computeFilterMatches,
  type FilterMetricRange,
  type FilterMetricRanges,
} from "../data/filter.js";
import { FILTER_BIO_RANGE_FILTERS, LIVE_FILTER_CHIPS, LIVE_FILTER_RANGE_FILTERS } from "../modules/filter-view.js";
import type { ActiveFilters, CategoricalActive, KitPlayer, RangeActive, RangeFilterConfig } from "../engines/stats-filter-kit.js";
import { useFilterOptions } from "./useFilterOptions.js";
import { LIVE } from "../live/flag.js";
import { distinctPlayers } from "../live/group-players.js";
import { filterMetricKeys, liveFilterMatches } from "../live/filter.js";
import { MINUTES_CHIP_DEFAULT } from "../live/stats-files.js";

const NO_REF: KitPlayer = {};

/** Every season code with real data across the population, newest-first. */
export function useFilterSeasonCodes(): string[] {
  return useMemo(() => filterSeasonCodes(readSnapshot().players), []);
}

/** Metric-by-key lookup for the sliders + the range predicate (built once). */
export function useFilterMetricByKey(): Map<string, ProtoPlayerMetric> {
  return useMemo(() => buildMetricByKey(getMetricCategories()), []);
}

/** A metric's 0-to-population-max slider domain for the current season + mode. */
export function useFilterMetricDomain(
  metric: ProtoPlayerMetric | null,
  seasonCode: string,
  mode: string,
): FilterMetricRange | null {
  return useMemo(
    () => (metric ? metricDomain(readSnapshot().players, metric, seasonCode, mode) : null),
    [metric, seasonCode, mode],
  );
}

export interface UseFilterResultsParams {
  activeBio: ActiveFilters;
  metricRanges: FilterMetricRanges;
  seasonCode: string;
  mode: string;
  refPlayerName: string | null;
  metricMap: Map<string, ProtoPlayerMetric>;
}

/** The matched population (sorted by name). `activeBio` + `metricRanges` are depended on by
 *  reference, so callers pass fresh objects when either set changes (the same in-place-mutate
 *  + version-bump bridge Rankings uses for its chips). */
export function useFilterResults({
  activeBio,
  metricRanges,
  seasonCode,
  mode,
  refPlayerName,
  metricMap,
}: UseFilterResultsParams): ProtoFilterResult[] {
  return useMemo(() => {
    const players = readSnapshot().players;
    const refPlayer = (refPlayerName && players[refPlayerName]) as unknown as KitPlayer;
    return computeFilterMatches(
      players,
      activeBio,
      refPlayer ?? NO_REF,
      seasonCode,
      metricRanges,
      mode,
      metricMap,
      FILTER_BIO_RANGE_FILTERS,
    );
  }, [activeBio, metricRanges, seasonCode, mode, refPlayerName, metricMap]);
}

// --- the widget's board (snapshot or live) ---------------------------------------------

export interface FilterBoardParams {
  /** The player the group is taken from (live: her league; snapshot: a chip's first value). */
  sourcePlayer: PlayerId | null;
  /** Live: the user removed the League chip (✕) — empty until a league is picked. */
  leagueCleared?: boolean;
  /** The season picked (`_filterSeason`); null or one without data → the default. */
  season: string | null;
  mode: string;
  activeBio: ActiveFilters;
  metricRanges: FilterMetricRanges;
  metricMap: Map<string, ProtoPlayerMetric>;
  /** Live: the Men's / Women's switch's pick (null = the player's gender). */
  womensPick?: boolean | null;
}

/** What the chip bar needs (`FilterChipBar`'s props). The optional ones are live only. */
export interface FilterChips {
  /** Live only: each value's heading + icon in its list (the League chip: country + flag). */
  optionInfo?: Record<string, (value: string) => OptionInfo | undefined>;
  options: Record<string, string[]>;
  refPlayer: KitPlayer;
  rangeFilters: RangeFilterConfig;
  addMenuKeys?: readonly string[];
  newChipValue?: (key: string) => CategoricalActive | RangeActive | undefined;
}

export interface FilterBoard {
  /** Live only: the League chip's default — the canvas player's league (Ben, 2026-10-16). */
  defaultLeague?: string;
  seasonCodes: string[];
  /** The season shown. */
  season: string | null;
  /** The population the sliders' ranges are measured on (the rows being matched). */
  players: Record<string, RawPlayerRecord>;
  entries: ProtoFilterResult[];
  /** "N players match" (distinct players); null = no group (live) — `label` says why. */
  count: number | null;
  /** The readout's words; undefined = the widget's default ("players match"). */
  label?: string;
  chips: FilterChips;
  /** The matched players' gender (a league is one gender) — a dragged row notes it, so a live
   *  Stats tile can take her (T2-14b). Null = no file shown (no rows either). */
  womens: boolean | null;
  /** Live only: the gender shown and the player's own (the Men's / Women's switch). */
  gender?: { shown: boolean | null; player: boolean | null };
}

function useSnapshotFilterBoard({ sourcePlayer, season, mode, activeBio, metricRanges, metricMap }: FilterBoardParams): FilterBoard {
  const seasonCodes = useFilterSeasonCodes();
  const shown = season && seasonCodes.includes(season) ? season : (seasonCodes[0] ?? null);
  const players = readSnapshot().players;
  const entries = useFilterResults({
    activeBio,
    metricRanges,
    seasonCode: shown ?? "",
    mode,
    refPlayerName: playerNameOf(players, sourcePlayer),
    metricMap,
  });
  const options = useFilterOptions();
  const refPlayer = useMemo<KitPlayer>(
    () => (playerById(readSnapshot().players, sourcePlayer) ?? {}) as unknown as KitPlayer,
    [sourcePlayer],
  );
  return {
    seasonCodes,
    season: shown,
    players,
    entries,
    count: entries.length,
    chips: { options, refPlayer, rangeFilters: FILTER_BIO_RANGE_FILTERS },
    // The snapshot holds women's leagues only (and the snapshot site has no gender rule).
    womens: true,
  };
}

// A League chip added from "+ Add filter" starts with nothing ticked (Ben, 2026-10-16: not the
// player's league, which a first tick would then add to). Minutes starts at 150+ (rules.md).
const liveNewChipValue = (key: string) =>
  key === "league" ? [] : key === "minutes" ? { ...MINUTES_CHIP_DEFAULT } : undefined;

function useLiveFilterBoard({ sourcePlayer, season, mode, activeBio, metricRanges, metricMap, leagueCleared, womensPick = null }: FilterBoardParams): FilterBoard {
  // Live always has one: the module falls back to the snapshot's first player (a real DB id).
  if (sourcePlayer === null) throw new Error("A live Filter needs a player to take its league from");
  // The League chip's leagues (useLeagueGroups: the canvas player's by default, tick others).
  const lg = useLiveLeagueGroups(sourcePlayer, season, activeBio.league as CategoricalActive | undefined, leagueCleared, womensPick);
  const key = groupsKey(lg);
  const byPosition = chipIsOn(activeBio.position);

  const refPlayer = useMemo<KitPlayer>(() => refPlayerOf(lg, sourcePlayer), [lg.home, sourcePlayer]);
  const options = useMemo(() => chipOptionsOfGroups(lg), [key, lg.leagueOptions]);
  // Every slider's metric, from the rows being matched (a Position chip → minutes AT each position),
  // so a slider's range spans exactly what it filters.
  const players = useMemo(
    () => (lg.problem !== null ? {} : playersOfGroups(lg, byPosition ? "position" : "total", filterMetricKeys(metricMap))),
    [lg.problem, key, byPosition, metricMap, lg.shown],
  );
  const entries = useMemo(
    () => liveFilterMatches(players, activeBio, refPlayer, lg.shown, metricRanges, mode, metricMap, byPosition),
    [players, activeBio, refPlayer, lg.shown, metricRanges, mode, metricMap, byPosition],
  );

  const chips: FilterChips = {
    options,
    refPlayer,
    rangeFilters: LIVE_FILTER_RANGE_FILTERS,
    // Q-013: only the chips at least half the players (with minutes) have data for.
    addMenuKeys: chipsWithData(LIVE_FILTER_CHIPS, filesOfGroups(lg)),
    newChipValue: liveNewChipValue,
    optionInfo: { league: leagueOptionInfo(lg.leagues) },
  };
  const base = {
    seasonCodes: lg.seasonCodes,
    season: lg.shown,
    chips,
    defaultLeague: lg.defaultLeague,
    gender: { shown: lg.womens, player: lg.playerWomens },
  };
  if (lg.problem !== null) {
    const label = lg.cleared ? "Pick a league" : `No players: ${lg.problem}`;
    return { ...base, players, entries: [], count: null, label, womens: null };
  }
  return {
    ...base,
    players,
    entries,
    count: distinctPlayers(entries),
    // The League chip says which league(s); a picked league with no file this season is named.
    label: lg.missingNote ? `players match · ${lg.missingNote}` : "players match",
    womens: lg.womens,
  };
}

/** The Filter widget's board: the snapshot's, or with ?live=1 her league's Stats file. */
export const useFilterBoard: (p: FilterBoardParams) => FilterBoard = LIVE ? useLiveFilterBoard : useSnapshotFilterBoard;
